// src/App.tsx - ОБНОВЛЕННАЯ ВЕРСИЯ с роутом Амбассадоры
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntdApp } from 'antd';
import ruRU from 'antd/locale/ru_RU';

// Auth Provider
import { AuthProvider } from '@/hooks/useAuth';

// Layouts
import { AdminLayout } from '@/shared/layouts/AdminLayout';

// Pages
import { Dashboard } from '@/modules/dashboard/Dashboard';
import { Orders } from '@/modules/orders/Orders';
import { Production } from '@/modules/production/Production';
import { Analytics } from '@/modules/analytics/Analytics';
import { Config } from '@/modules/config/Config';
import { SettingsPage } from '@/modules/settings';
import { Feedbacks } from '@/modules/feedbacks';
import { Chats } from '@/modules/chats';
import { Ambassadors } from '@/modules/ambassadors'; // ✨ НОВЫЙ ИМПОРТ
import { Login } from '@/modules/auth/Login';
import { Unauthorized } from '@/modules/auth/Unauthorized';

// Защищенный роут
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={ruRU}>
        <AntdApp>
          <AuthProvider>
            <Router>
              <Routes>
                {/* Публичные маршруты */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                
                {/* Защищенные маршруты */}
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  
                  {/* Дашборд - доступен всем ролям */}
                  <Route 
                    path="dashboard" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_view_orders_dashboard']}>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Заказы - Admin, Manager, Operator */}
                  <Route 
                    path="orders" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_orders']}>
                        <Orders />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Отзывы - Admin, Manager */}
                  <Route 
                    path="feedbacks" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_orders']}>
                        <Feedbacks />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Чаты - Admin */}
                  <Route 
                    path="chats" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_orders']}>
                        <Chats />
                      </ProtectedRoute>
                    } 
                  />

                  {/* ✨ НОВЫЙ РОУТ: Амбассадоры - только Admin */}
                  <Route 
                    path="ambassadors" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_users']}>
                        <Ambassadors />
                      </ProtectedRoute>
                    } 
                  />

                  {/* Производство - Admin, Kitchen */}
                  <Route 
                    path="production" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_manufacturing']}>
                        <Production />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Аналитика - Admin, Manager */}
                  <Route 
                    path="analytics" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_view_full_dashboard']}>
                        <Analytics />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Конфигурация каталога - Admin, Manager */}
                  <Route 
                    path="config" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_catalog']}>
                        <Config />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Настройки системы - только Admin */}
                  <Route 
                    path="settings" 
                    element={
                      <ProtectedRoute requiredPermissions={['can_manage_users']}>
                        <SettingsPage />
                      </ProtectedRoute>
                    } 
                  />
                </Route>
                
                {/* Fallback для неавторизованных */}
                <Route path="*" element={<Navigate to="/auth/login" replace />} />
              </Routes>
            </Router>
          </AuthProvider>
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

export default App;